import { useState, useEffect } from 'react';
import { apiClient } from '../../api/client';

interface ChapterBreakdown {
  chapter: string;
  count: number;
  withSeats: number;
  withoutSeats: number;
  withMeal: number;
  withoutMeal: number;
}

interface RegistrationStats {
  totalRegistered: number;
  ticketed: number;
  nonTicketed: number;
  withSeats: number;
  withoutSeats: number;
  checkedIn: number;
  confirmedOrders: number;
  pendingOrders: number;
  byChapter?: ChapterBreakdown[];
}

const panelStyle: React.CSSProperties = {
  padding: '16px',
  paddingBottom: '80px',
};

const titleStyle: React.CSSProperties = {
  fontSize: '20px',
  fontWeight: 700,
  color: '#fff',
  margin: '0 0 16px 0',
};

const statCardStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '14px 16px',
  background: '#122b1c',
  borderRadius: '8px',
  marginBottom: '8px',
};

const statLabelStyle: React.CSSProperties = {
  fontSize: '14px',
  color: '#a0a0a0',
};

const statValueStyle: React.CSSProperties = {
  fontSize: '20px',
  fontWeight: 700,
  color: '#c9a84c',
};

const heroCardStyle: React.CSSProperties = {
  padding: '20px',
  background: 'linear-gradient(135deg, #1a4a2e 0%, #0d2418 100%)',
  borderRadius: '12px',
  marginBottom: '16px',
  textAlign: 'center' as const,
  border: '1px solid #2a5a3e',
};

const heroValueStyle: React.CSSProperties = {
  fontSize: '48px',
  fontWeight: 800,
  color: '#c9a84c',
  lineHeight: 1,
};

const heroLabelStyle: React.CSSProperties = {
  fontSize: '14px',
  color: '#a0a0a0',
  marginTop: '4px',
};

const loadingStyle: React.CSSProperties = {
  textAlign: 'center' as const,
  color: '#a0a0a0',
  padding: '40px 0',
  fontSize: '14px',
};

const errorStyle: React.CSSProperties = {
  padding: '12px 16px',
  background: '#5c1a1a',
  borderRadius: '8px',
  color: '#ffb3b3',
  fontSize: '13px',
};

export function RegistrationStatsPanel() {
  const [stats, setStats] = useState<RegistrationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function fetch() {
      try {
        const data = await apiClient<RegistrationStats>('/legacy-ball/registration-stats');
        setStats(data);
      } catch {
        setError('Could not load stats.');
      } finally {
        setLoading(false);
      }
    }
    fetch();
  }, []);

  if (loading) return <div style={loadingStyle}>Loading stats...</div>;
  if (error) return <div style={panelStyle}><div style={errorStyle}>{error}</div></div>;
  if (!stats) return null;

  return (
    <div style={panelStyle}>
      <h2 style={titleStyle}>📊 Registration Stats</h2>

      <div style={heroCardStyle}>
        <div style={heroValueStyle}>{stats.totalRegistered}</div>
        <div style={heroLabelStyle}>Total Registered</div>
      </div>

      <div style={statCardStyle}>
        <span style={statLabelStyle}>🎟️ With Tickets</span>
        <span style={statValueStyle}>{stats.ticketed}</span>
      </div>
      <div style={statCardStyle}>
        <span style={statLabelStyle}>📋 Without Tickets</span>
        <span style={statValueStyle}>{stats.nonTicketed}</span>
      </div>
      <div style={statCardStyle}>
        <span style={statLabelStyle}>🪑 Seated</span>
        <span style={statValueStyle}>{stats.withSeats}</span>
      </div>
      <div style={statCardStyle}>
        <span style={statLabelStyle}>🚶 No Seat</span>
        <span style={statValueStyle}>{stats.withoutSeats}</span>
      </div>
      <div style={statCardStyle}>
        <span style={statLabelStyle}>✅ Checked In</span>
        <span style={statValueStyle}>{stats.checkedIn}</span>
      </div>
      <div style={statCardStyle}>
        <span style={statLabelStyle}>📦 Confirmed Orders</span>
        <span style={statValueStyle}>{stats.confirmedOrders}</span>
      </div>
      <div style={statCardStyle}>
        <span style={statLabelStyle}>⏳ Pending Orders</span>
        <span style={statValueStyle}>{stats.pendingOrders}</span>
      </div>

      {/* Chapter Breakdown Table */}
      <div style={{ marginTop: '20px', borderTop: '1px solid #2a5a3e', paddingTop: '16px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#c9a84c', margin: '0 0 12px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          📍 By Chapter
        </h3>
        {stats.byChapter && stats.byChapter.length > 0 ? (
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: '420px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #2a5a3e' }}>
                  <th style={{ textAlign: 'left', padding: '8px 6px', fontWeight: 600, color: '#a0a0a0' }}>Chapter</th>
                  <th style={{ textAlign: 'center', padding: '8px 4px', fontWeight: 600, color: '#a0a0a0' }}>Total</th>
                  <th style={{ textAlign: 'center', padding: '8px 4px', fontWeight: 600, color: '#4caf50' }}>Seated</th>
                  <th style={{ textAlign: 'center', padding: '8px 4px', fontWeight: 600, color: '#ef5350' }}>No Seat</th>
                  <th style={{ textAlign: 'center', padding: '8px 4px', fontWeight: 600, color: '#4caf50' }}>Meal</th>
                  <th style={{ textAlign: 'center', padding: '8px 4px', fontWeight: 600, color: '#ef5350' }}>No Meal</th>
                </tr>
              </thead>
              <tbody>
                {stats.byChapter.map((item) => (
                  <tr key={item.chapter} style={{ borderBottom: '1px solid #1a3d2a' }}>
                    <td style={{ padding: '8px 6px', color: '#000', fontWeight: 600 }}>{item.chapter}</td>
                    <td style={{ textAlign: 'center', padding: '8px 4px', color: '#c9a84c', fontWeight: 700 }}>{item.count}</td>
                    <td style={{ textAlign: 'center', padding: '8px 4px', color: '#4caf50' }}>{item.withSeats}</td>
                    <td style={{ textAlign: 'center', padding: '8px 4px', color: item.withoutSeats > 0 ? '#ef5350' : '#666' }}>{item.withoutSeats}</td>
                    <td style={{ textAlign: 'center', padding: '8px 4px', color: '#4caf50' }}>{item.withMeal}</td>
                    <td style={{ textAlign: 'center', padding: '8px 4px', color: item.withoutMeal > 0 ? '#ef5350' : '#666' }}>{item.withoutMeal}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ fontSize: '13px', color: '#666', margin: 0 }}>No chapter data available</p>
        )}
      </div>
    </div>
  );
}
